import {
  Box,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Step,
  StepIcon,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  Stepper,
  Text,
  useBreakpointValue,
  useSteps,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useContracts } from '../../contexts/ContractsContext';
import { contractsService } from '../../services/contracts.service';
import { collectSignatureContext } from '../../services/deviceInfo.service';
import { ContractSummary } from '../../services/types/contracts.type';
import { ContractStepConfirm } from './ContractStepConfirm';
import { ContractStepIdentity } from './ContractStepIdentity';
import { ContractStepRead } from './ContractStepRead';
import { ContractStepSign } from './ContractStepSign';

const STEPS = [
  { title: 'Leitura' },
  { title: 'Identidade' },
  { title: 'Assinatura' },
  { title: 'Confirmação' },
];

export function ContractGateModal() {
  const { status, showGate, dismissForSession, refresh } = useContracts();
  const { activeStep, setActiveStep } = useSteps({ index: 0, count: STEPS.length });
  const [contract, setContract] = useState<ContractSummary | null>(null);
  const [loadingContract, setLoadingContract] = useState(false);
  const [signing, setSigning] = useState(false);
  const [emailCode, setEmailCode] = useState('');
  const [signatureImage, setSignatureImage] = useState('');
  const isMobile = useBreakpointValue({ base: true, md: false });
  const toast = useToast();

  const isAddendum = !!status?.pendingAddendum && !status?.pendingContract;
  const targetId = isAddendum ? status?.pendingAddendum?.id : status?.pendingContract?.id;
  const blocked = !!status?.blocked;
  const noActiveTemplate = !!status?.noActiveTemplate;

  // Fetch contract content when the modal is shown
  useEffect(() => {
    if (!showGate || !targetId || isAddendum) return;
    let cancelled = false;
    setLoadingContract(true);
    contractsService
      .getById(targetId)
      .then((c) => {
        if (!cancelled) setContract(c);
      })
      .finally(() => {
        if (!cancelled) setLoadingContract(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showGate, targetId, isAddendum]);

  // For addendums, pull the content via the contracts list (find the addendum inside its contract)
  useEffect(() => {
    if (!showGate || !isAddendum || !targetId) return;
    let cancelled = false;
    setLoadingContract(true);
    contractsService
      .listMine()
      .then((contracts) => {
        if (cancelled) return;
        for (const c of contracts) {
          const found = c.addendums?.find((a) => a.id === targetId);
          if (found) {
            // Fake a contract-like object with the addendum's content snapshot for the renderer
            setContract({
              ...(c as any),
              id: found.id,
              contentSnapshot: found.contentSnapshot,
            });
            return;
          }
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingContract(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showGate, isAddendum, targetId]);

  // Special case: customer is required but the company has no ACTIVE template yet.
  // Show a clear admin-facing error instead of silently rendering nothing.
  if (showGate && !targetId && noActiveTemplate) {
    return (
      <Modal
        isOpen
        onClose={() => undefined}
        closeOnOverlayClick={false}
        closeOnEsc={false}
        size="lg"
        isCentered
      >
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
        <ModalContent
          bg="#fefcf7"
          borderRadius="2px"
          border="1px solid"
          borderColor="rgba(28, 25, 22, 0.12)"
        >
          <ModalHeader pt={8} pb={2} px={8}>
            <Text
              fontSize="10px"
              letterSpacing="0.28em"
              textTransform="uppercase"
              color="#988a78"
              fontWeight="700"
              mb={2}
            >
              Documento Indisponível
            </Text>
            <Heading
              fontFamily='"Fraunces", "Playfair Display", Georgia, serif'
              fontSize="26px"
              fontWeight="500"
              color="#1c1916"
              letterSpacing="-0.02em"
            >
              Contrato não disponível
            </Heading>
          </ModalHeader>
          <ModalBody pb={8} px={8}>
            <Text fontSize="14px" color="#4a3f30" lineHeight="1.6">
              Sua empresa exige assinatura de contrato, mas o administrador
              ainda não publicou um template ativo. Entre em contato com o
              suporte da plataforma para regularizar.
            </Text>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  if (!showGate || !targetId) return null;

  const handleSendCode = async () => {
    if (isAddendum) {
      await contractsService.requestAddendumEmailCode(targetId);
    } else {
      await contractsService.requestEmailCode(targetId);
    }
  };

  const handleSign = async () => {
    setSigning(true);
    try {
      const ctx = await collectSignatureContext();
      const payload = {
        emailCode,
        signatureImage,
        deviceFingerprint: ctx.deviceFingerprint,
        deviceInfo: ctx.deviceInfo,
        geoLocation: ctx.geoLocation,
      };
      if (isAddendum) {
        await contractsService.signAddendum(targetId, payload);
      } else {
        await contractsService.sign(targetId, payload);
      }
      toast({ title: 'Contrato assinado com sucesso', status: 'success', duration: 4000 });
      await refresh();
      setActiveStep(0);
      setContract(null);
      setEmailCode('');
      setSignatureImage('');
    } catch (err: any) {
      toast({
        title: 'Erro ao assinar',
        description: err?.response?.data?.message || 'Tente novamente',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSigning(false);
    }
  };

  const deadline = status?.pendingContract?.deadline
    ? new Date(status.pendingContract.deadline).toLocaleString('pt-BR')
    : null;

  return (
    <Modal
      isOpen={showGate}
      onClose={() => {
        if (!blocked) dismissForSession();
      }}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      size={isMobile ? 'full' : '4xl'}
      isCentered={!isMobile}
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent
        maxH={isMobile ? '100vh' : '90vh'}
        bg="#fefcf7"
        borderRadius="2px"
        border="1px solid"
        borderColor="rgba(28, 25, 22, 0.12)"
        boxShadow="0 30px 60px -25px rgba(28, 25, 22, 0.5)"
      >
        <ModalHeader pt={8} pb={4} px={{ base: 6, md: 10 }}>
          <Text
            fontSize="10px"
            letterSpacing="0.28em"
            textTransform="uppercase"
            color="#988a78"
            fontWeight="700"
            mb={2}
          >
            {isAddendum ? 'Termo Aditivo' : 'Documento Jurídico'}
          </Text>
          <Heading
            as="h2"
            fontFamily='"Fraunces", "Playfair Display", Georgia, serif'
            fontSize={{ base: '24px', md: '32px' }}
            fontWeight="500"
            color="#1c1916"
            lineHeight="1.05"
            letterSpacing="-0.02em"
          >
            {isAddendum
              ? 'Assinatura do Termo Aditivo'
              : 'Assinatura de Contrato'}
          </Heading>
          <Box
            mt={3}
            h="1px"
            bgGradient="linear(to-r, blackAlpha.500, transparent)"
            maxW="100px"
          />
          {deadline && !blocked && (
            <Text fontSize="13px" color="#7a6f60" fontWeight="normal" mt={4}>
              Você pode adiar a assinatura até <strong>{deadline}</strong>.
            </Text>
          )}
          {blocked && (
            <Text fontSize="13px" color="#8a3a3a" fontWeight="normal" mt={4} lineHeight="1.5">
              É necessário assinar este contrato para continuar usando saques,
              devoluções e demais operações da plataforma.
            </Text>
          )}
        </ModalHeader>
        {!blocked && <ModalCloseButton color="#7a6f60" />}
        <ModalBody pb={8} px={{ base: 6, md: 10 }}>
          <Stack spacing={7}>
            <Stepper
              index={activeStep}
              size={isMobile ? 'sm' : 'md'}
              colorScheme="orange"
            >
              {STEPS.map((s, i) => (
                <Step key={i}>
                  <StepIndicator>
                    <StepStatus
                      complete={<StepIcon />}
                      incomplete={<StepNumber />}
                      active={<StepNumber />}
                    />
                  </StepIndicator>
                  <StepTitle>{s.title}</StepTitle>
                  <StepSeparator />
                </Step>
              ))}
            </Stepper>

            {loadingContract && (
              <Text fontStyle="italic" color="#7a6f60" fontSize="14px">
                Carregando contrato…
              </Text>
            )}

            {!loadingContract && contract && activeStep === 0 && (
              <ContractStepRead snapshot={contract.contentSnapshot} onContinue={() => setActiveStep(1)} />
            )}
            {!loadingContract && contract && activeStep === 1 && (
              <ContractStepIdentity onSendCode={handleSendCode} onContinue={() => setActiveStep(2)} />
            )}
            {!loadingContract && contract && activeStep === 2 && (
              <ContractStepSign
                onSubmit={({ emailCode: code, signatureImage: img }) => {
                  setEmailCode(code);
                  setSignatureImage(img);
                  setActiveStep(3);
                }}
              />
            )}
            {!loadingContract && contract && activeStep === 3 && (
              <ContractStepConfirm
                signatureImage={signatureImage}
                signing={signing}
                onConfirm={handleSign}
                onBack={() => setActiveStep(2)}
              />
            )}
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
